<template>
  <div class="mod-overlay" @click.self="$emit('close')">
    <div class="mod-modal emp-modal">
      <!-- Header -->
      <div class="mod-modal-header">
        <h3 class="mod-modal-title">
          <i :class="isEdit ? 'bi bi-pencil' : 'bi bi-plus-circle'"></i>
          {{ isEdit ? 'Editar empresa' : 'Nueva empresa' }}
        </h3>
        <button class="mod-modal-close" @click="$emit('close')">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Tabs -->
      <div class="emp-tabs">
        <button class="emp-tab" :class="{ 'emp-tab-active': activeTab === 'datos' }"
          type="button" @click="activeTab = 'datos'">
          <i class="bi bi-building"></i> Datos
        </button>
        <button class="emp-tab" :class="{ 'emp-tab-active': activeTab === 'fiscal' }"
          type="button" :disabled="!isEdit"
          :title="!isEdit ? 'Disponible al editar una empresa existente' : ''"
          @click="onTabFiscal">
          <i class="bi bi-receipt"></i> Fiscal
          <span v-if="!isEdit" class="emp-tab-lock"><i class="bi bi-lock-fill"></i></span>
        </button>
        <button class="emp-tab" :class="{ 'emp-tab-active': activeTab === 'erp' }"
          type="button" :disabled="!isEdit"
          :title="!isEdit ? 'Disponible al editar una empresa existente' : ''"
          @click="onTabErp">
          <i class="bi bi-plug"></i> ERP Externo
          <span v-if="!isEdit" class="emp-tab-lock"><i class="bi bi-lock-fill"></i></span>
        </button>
      </div>

      <form @submit.prevent="handleSubmit">
        <div v-if="error" class="mod-alert-error" style="margin: 0.75rem 1.25rem 0;">
          <i class="bi bi-exclamation-triangle-fill"></i> {{ error }}
        </div>

        <div class="mod-modal-body" style="padding-top: 0.5rem;">

          <!-- ── Tab: Datos ─────────────────────────────────────────── -->
          <div v-show="activeTab === 'datos'">
            <div class="mod-form-grid">
              <div class="mod-field">
                <label for="EmpresaId">
                  EmpresaId
                  <span v-if="!isEdit" class="mod-required">*</span>
                </label>
                <input type="number" id="EmpresaId"
                  v-model.number="form.EmpresaId"
                  :readonly="isEdit" :disabled="isEdit"
                  :required="!isEdit" min="1"
                  placeholder="Número identificador de empresa">
                <small v-if="!isEdit" class="mod-help">
                  La PK no es automática: captura el número que esta empresa tendrá.
                </small>
              </div>
              <div class="mod-field">
                <label for="Codigo">Código <span class="mod-required">*</span></label>
                <input type="text" id="Codigo" v-model="form.Codigo" required maxlength="50" placeholder="Código de la empresa">
              </div>
              <div class="mod-field">
                <label for="Nombre">Nombre <span class="mod-required">*</span></label>
                <input type="text" id="Nombre" v-model="form.Nombre" required placeholder="Nombre de la empresa">
              </div>
              <div class="mod-field">
                <label for="ClaveFiscal">RFC / Clave Fiscal</label>
                <input type="text" id="ClaveFiscal" v-model="form.ClaveFiscal" maxlength="20" placeholder="RFC">
              </div>
              <div class="mod-field">
                <label for="Telefono">Teléfono</label>
                <input type="text" id="Telefono" v-model="form.Telefono" maxlength="30" placeholder="Teléfono">
              </div>
              <div class="mod-field mod-field-full">
                <label for="CalleNumero">Calle y Número</label>
                <input type="text" id="CalleNumero" v-model="form.CalleNumero" placeholder="Calle y número">
              </div>
              <div class="mod-field">
                <label for="ColoniaCodigoPostal">Colonia / C.P.</label>
                <input type="text" id="ColoniaCodigoPostal" v-model="form.ColoniaCodigoPostal" placeholder="Colonia y código postal">
              </div>
              <div class="mod-field">
                <label for="Ciudad">Ciudad</label>
                <input type="text" id="Ciudad" v-model="form.Ciudad" placeholder="Ciudad">
              </div>
              <div class="mod-field">
                <label for="PaisOperacion">País de operación</label>
                <input type="text" id="PaisOperacion" v-model="configEmpresa.Pais" maxlength="10" placeholder="MEX">
              </div>
              <div class="mod-field">
                <label for="MonedaNacional">Moneda nacional</label>
                <input type="text" id="MonedaNacional" v-model="configEmpresa.MonedaNacional" maxlength="5" placeholder="MXN">
              </div>
              <div class="mod-field mod-field-full">
                <label>Clave de socios de negocio</label>
                <select v-model="configEmpresa.ModoClaveSocio" class="mod-select">
                  <option value="MANUAL">Manual</option>
                  <option value="AUTOMATICA">Automática</option>
                </select>
                <small class="mod-help">
                  Manual: se captura la clave. Automática: clientes `c1`, `c2` y proveedores `p1`, `p2`.
                </small>
              </div>
              <div class="mod-field mod-field-full">
                <label for="Licencia">Licencia</label>
                <input type="text" id="Licencia" v-model="form.Licencia" placeholder="Clave de licencia">
              </div>
              <div class="mod-field mod-field-check">
                <label class="mod-check-label">
                  <input type="checkbox" v-model="form.EsActivo">
                  <span>Activo</span>
                </label>
              </div>
            </div>
          </div>

          <!-- ── Tab: Fiscal ───────────────────────────────────────── -->
          <div v-show="activeTab === 'fiscal'">
            <div v-if="loadingFiscal" class="mod-loading" style="padding: 2rem 0;">
              <span class="mod-spinner"></span> Cargando datos fiscales...
            </div>
            <div v-else>
              <div class="emp-erp-header">
                <div class="emp-erp-status" :class="fiscal.EmpresaId ? 'emp-erp-status-ok' : 'emp-erp-status-empty'">
                  <i :class="fiscal.EmpresaId ? 'bi bi-check-circle-fill' : 'bi bi-dash-circle'"></i>
                  {{ fiscal.EmpresaId ? 'Datos fiscales registrados' : 'Sin datos fiscales' }}
                </div>
                <div style="display:flex; gap:0.5rem; align-items:center;">
                  <label class="mod-check-label" style="font-size:0.84rem;">
                    <input type="checkbox" v-model="fiscal.EsActivo">
                    <span>Timbrado activo</span>
                  </label>
                  <button v-if="fiscal.EmpresaId" type="button" class="emp-btn-danger"
                    :disabled="savingFiscal" @click="borrarFiscal">
                    <i class="bi bi-trash"></i> Eliminar
                  </button>
                </div>
              </div>

              <div class="mod-form-grid">

                <!-- Identidad fiscal -->
                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-person-vcard"></i> Identidad fiscal
                </div>

                <div class="mod-field mod-field-full">
                  <label>Razón Social <span class="mod-required">*</span></label>
                  <input type="text" v-model="fiscal.RazonSocial" maxlength="300"
                    placeholder="Nombre fiscal exacto (como aparece en el SAT)">
                </div>
                <div class="mod-field">
                  <label>Régimen Fiscal</label>
                  <select v-model="fiscal.RegimenFiscal" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option v-for="r in regimenesFiscales" :key="r.Clave" :value="r.Clave">
                      {{ r.Clave }} - {{ r.Nombre }}
                    </option>
                  </select>
                </div>
                <div class="mod-field">
                  <label>Código Postal Fiscal</label>
                  <input type="text" v-model="fiscal.CodigoPostalFiscal" maxlength="10"
                    placeholder="CP domicilio fiscal (CFDI 4.0)">
                </div>

                <!-- CSD -->
                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-shield-lock"></i> Certificado de Sello Digital (CSD)
                </div>

                <div class="mod-field">
                  <label>Archivo .cer</label>
                  <input type="text" v-model="fiscal.CsdCerRuta" maxlength="500"
                    placeholder="Ruta o nombre del archivo .cer">
                </div>
                <div class="mod-field">
                  <label>Archivo .key</label>
                  <input type="text" v-model="fiscal.CsdKeyRuta" maxlength="500"
                    placeholder="Ruta o nombre del archivo .key">
                </div>
                <div class="mod-field">
                  <label>Password CSD</label>
                  <input :type="revealPasswords ? 'text' : 'password'" v-model="fiscal.CsdPassword" maxlength="200"
                    placeholder="Contraseña del certificado">
                </div>
                <div class="mod-field">
                  <label>Número de certificado</label>
                  <input type="text" v-model="fiscal.CsdNumero" maxlength="40"
                    placeholder="20 dígitos emitidos por el SAT">
                </div>
                <div class="mod-field">
                  <label>Vigencia CSD</label>
                  <input type="date" v-model="fiscal.CsdVigencia">
                </div>

                <!-- PAC -->
                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-cloud-check"></i> PAC (Proveedor Autorizado de Certificación)
                </div>

                <div class="mod-field">
                  <label>PAC</label>
                  <select v-model="fiscal.PacNombre" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option value="FINKOK">Finkok</option>
                    <option value="SW">SW Sapien</option>
                    <option value="DIVERZA">Diverza</option>
                    <option value="ECODEX">Ecodex</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
                <div class="mod-field">
                  <label>Ambiente</label>
                  <select v-model="fiscal.PacAmbiente" class="mod-select">
                    <option value="PRUEBAS">Pruebas</option>
                    <option value="PRODUCCION">Producción</option>
                  </select>
                </div>
                <div class="mod-field mod-field-full">
                  <label>URL del web service</label>
                  <input type="text" v-model="fiscal.PacUrl" maxlength="500"
                    placeholder="https://api.pac.com/cfdi/stamp">
                </div>
                <div class="mod-field">
                  <label>Usuario PAC</label>
                  <input type="text" v-model="fiscal.PacUsuario" maxlength="100">
                </div>
                <div class="mod-field">
                  <label>Password PAC</label>
                  <input :type="revealPasswords ? 'text' : 'password'" v-model="fiscal.PacPassword" maxlength="200" placeholder="••••••••">
                </div>

                <!-- Defaults de facturación -->
                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-sliders"></i> Defaults de facturación
                </div>

                <div class="mod-field">
                  <label>Serie por defecto</label>
                  <input type="text" v-model="fiscal.SerieDefecto" maxlength="10" placeholder="A">
                </div>
                <div class="mod-field">
                  <label>Uso CFDI por defecto</label>
                  <select v-model="fiscal.UsoCfdiDefecto" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option value="G01">G01 - Adquisición de mercancias</option>
                    <option value="G03">G03 - Gastos en general</option>
                    <option value="P01">P01 - Por definir</option>
                    <option value="S01">S01 - Sin efectos fiscales</option>
                    <option value="CP01">CP01 - Pagos</option>
                  </select>
                </div>
                <div class="mod-field">
                  <label>Método de pago</label>
                  <select v-model="fiscal.MetodoPago" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option value="PUE">PUE - Pago en una sola exhibición</option>
                    <option value="PPD">PPD - Pago en parcialidades o diferido</option>
                  </select>
                </div>
                <div class="mod-field">
                  <label>Forma de pago</label>
                  <select v-model="fiscal.FormaPago" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option value="01">01 - Efectivo</option>
                    <option value="02">02 - Cheque nominativo</option>
                    <option value="03">03 - Transferencia electrónica</option>
                    <option value="04">04 - Tarjeta de crédito</option>
                    <option value="28">28 - Tarjeta de débito</option>
                    <option value="99">99 - Por definir</option>
                  </select>
                </div>
                <div class="mod-field">
                  <label>Moneda</label>
                  <select v-model="fiscal.Moneda" class="mod-select">
                    <option value="MXN">MXN - Peso Mexicano</option>
                    <option value="USD">USD - Dólar Americano</option>
                    <option value="EUR">EUR - Euro</option>
                  </select>
                </div>

              </div>
            </div>
          </div>

          <!-- ── Tab: ERP Externo ───────────────────────────────────── -->
          <div v-show="activeTab === 'erp'">
            <div v-if="loadingErp" class="mod-loading" style="padding: 2rem 0;">
              <span class="mod-spinner"></span> Cargando configuración ERP...
            </div>
            <div v-else>
              <div class="emp-erp-header">
                <div class="emp-erp-status" :class="erp.EmpresaId ? 'emp-erp-status-ok' : 'emp-erp-status-empty'">
                  <i :class="erp.EmpresaId ? 'bi bi-check-circle-fill' : 'bi bi-dash-circle'"></i>
                  {{ erp.EmpresaId ? 'Configuración registrada' : 'Sin configuración ERP' }}
                </div>
                <div style="display:flex; gap:0.5rem; align-items:center;">
                  <label class="mod-check-label" style="font-size:0.84rem;">
                    <input type="checkbox" v-model="erp.EsActivo">
                    <span>Activo</span>
                  </label>
                  <button type="button" class="emp-btn-test"
                    :disabled="savingErp || testingErp || !erp.EmpresaId"
                    :title="!erp.EmpresaId ? 'Primero guarda la configuración ERP' : 'Probar conexión al ERP'"
                    @click="probarConexionErp">
                    <template v-if="testingErp">
                      <span class="mod-spinner-sm"></span> Probando...
                    </template>
                    <template v-else>
                      <i class="bi bi-plug-fill"></i> Probar Conexión
                    </template>
                  </button>
                  <button v-if="erp.EmpresaId" type="button" class="emp-btn-danger"
                    :disabled="savingErp" @click="borrarErp">
                    <i class="bi bi-trash"></i> Eliminar
                  </button>
                </div>
              </div>

              <div class="mod-form-grid">
                <div class="mod-field">
                  <label>Tipo de ERP <span class="mod-required">*</span></label>
                  <select v-model="erp.ErpType" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option value="G360">G360 — Gestión 360</option>
                    <option value="SAP_B1">SAP Business One</option>
                    <option value="SAP_ECC">SAP ECC / S4HANA</option>
                    <option value="ODOO">Odoo</option>
                    <option value="MICROSIP">Microsip</option>
                    <option value="COI">COI / Aspel</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
                <div class="mod-field">
                  <label>Host / Servidor</label>
                  <input type="text" v-model="erp.Host" maxlength="200" placeholder="192.168.1.10">
                </div>
                <div class="mod-field">
                  <label>Puerto</label>
                  <input type="text" v-model="erp.Port" placeholder="50000">
                </div>
                <div class="mod-field" v-if="!esSapB1">
                  <label>Instancia</label>
                  <input type="text" v-model="erp.Instance" maxlength="100" placeholder="MSSQLSERVER / PRD">
                </div>
                <div class="mod-field" v-if="!esSapB1">
                  <label>Client / Mandante</label>
                  <input type="text" v-model="erp.Client" maxlength="20" placeholder="100">
                </div>
                <div class="mod-field">
                  <label>Servidor de Licencias <span v-if="esSapB1" class="mod-required">*</span></label>
                  <input type="text" v-model="erp.ServerLicence" maxlength="200" placeholder="servidor:30000">
                </div>

                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-file-earmark-text"></i> Comportamiento de documentos
                </div>

                <div class="mod-field mod-field-full">
                  <label class="mod-check-label">
                    <input type="checkbox" v-model="erp.TipoBsPartida">
                    <span>Capturar Bienes/Servicios por partida</span>
                  </label>
                  <small class="mod-help">
                    Desactivado: el tipo B/S se define en cabecera. Activado: cada partida permite elegir Bienes o Servicios.
                  </small>
                </div>

                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-database"></i> Base de datos externa
                </div>

                <div class="mod-field">
                  <label>Nombre BD / SystemId</label>
                  <input type="text" v-model="erp.DbName" maxlength="100" placeholder="SBO_NombreEmpresa">
                </div>
                <div class="mod-field">
                  <label>Usuario BD</label>
                  <input type="text" v-model="erp.DbUser" maxlength="80" placeholder="sa">
                </div>
                <div class="mod-field">
                  <label>Password BD</label>
                  <input :type="revealPasswords ? 'text' : 'password'" v-model="erp.DbPassword" maxlength="200" placeholder="••••••••">
                </div>
                <div class="mod-field">
                  <label>Tipo de BD</label>
                  <select v-model="erp.DbType" class="mod-select">
                    <option value="">— Seleccionar —</option>
                    <option value="MSSQL2012">7-SQL Server 2012</option>
                    <option value="MSSQL2016">10-SQL Server 2016</option>
                    <option value="MSSQL2017">11-SQL Server 2017</option>
                    <option value="MSSQL2019">15-SQL Server 2019</option>
                    <option value="HANADB">9-SAP HANA</option>
                    <option v-if="!esSapB1" value="POSTGRES">PostgreSQL</option>
                    <option v-if="!esSapB1" value="MYSQL">MySQL</option>
                    <option v-if="!esSapB1" value="ORACLE">Oracle</option>
                  </select>
                </div>
                <div class="mod-field" v-if="!esSapB1">
                  <label>Driver / Provider</label>
                  <input type="text" v-model="erp.DbDriver" maxlength="100" placeholder="System.Data.SqlClient">
                </div>

                <div class="mod-field mod-field-full emp-section-title">
                  <i class="bi bi-person-badge"></i> Usuario global del ERP
                </div>

                <div class="mod-field">
                  <label>Usuario ERP</label>
                  <input type="text" v-model="erp.ErpUser" maxlength="80" placeholder="manager">
                </div>
                <div class="mod-field">
                  <label>Password ERP</label>
                  <input :type="revealPasswords ? 'text' : 'password'" v-model="erp.ErpPassword" maxlength="200" placeholder="••••••••">
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="mod-modal-footer">
          <button type="button" class="mod-btn mod-btn-cancel" @click="$emit('close')">Cancelar</button>
          <button type="submit" class="mod-btn mod-btn-save" :disabled="isSaving || isLoading">
            <span v-if="isSaving" class="mod-spinner-sm"></span>
            <template v-else>
              <i class="bi bi-check-lg"></i>
              {{ labelGuardar }}
            </template>
          </button>
        </div>
      </form>
    </div>

    <!-- Overlay: probando conexión ERP -->
    <transition name="erp-test-fade">
      <div v-if="testingErp" class="erp-test-overlay">
        <div class="erp-test-card">
          <div class="erp-test-spinner"></div>
          <p class="erp-test-msg">Probando conexión con el ERP...</p>
          <p class="erp-test-sub">{{ erp.Host }}{{ erp.Port ? ':' + erp.Port : '' }}</p>
        </div>
      </div>
    </transition>

    <!-- Overlay: guardando -->
    <transition name="erp-test-fade">
      <div v-if="isSaving" class="erp-test-overlay">
        <div class="erp-test-card">
          <div class="erp-test-spinner"></div>
          <p class="erp-test-msg">Procesando...</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { showToast } from '@/shared/utils/toast';
import { AlertService } from '@/shared/utils/alert';
import { getApiUrl } from '@/config/api';
import empresaService from './empresa.service';
import empresaErpExternoService from './empresaErpExterno.service';
import empresaFiscalService from './empresaFiscal.service';
import { createEmpresa } from './empresa.model';
import parametroService from '@/modules/parametros/parametro.service';

const PARAM_EMP_PAIS = 'EMP_PAIS';
const PARAM_EMP_MONEDA_NACIONAL = 'EMP_MONEDA_NACIONAL';
const PARAM_SOCIO_CLAVE_MODO = 'SOCIO_CLAVE_MODO';

function createErp() {
  return { EmpresaId: 0, ErpType: '', Host: '', Port: null, Instance: '', ServerLicence: '',
    Client: '', DbName: '', DbUser: '', DbPassword: '', DbType: '', DbDriver: '',
    ErpUser: '', ErpPassword: '', TipoBsPartida: false, EsActivo: true };
}

function createFiscal() {
  return { EmpresaId: 0, RazonSocial: '', RegimenFiscal: '', CodigoPostalFiscal: '',
    CsdCerRuta: '', CsdKeyRuta: '', CsdPassword: '', CsdVigencia: null, CsdNumero: '',
    PacNombre: '', PacUrl: '', PacUsuario: '', PacPassword: '', PacAmbiente: 'PRUEBAS',
    SerieDefecto: '', UsoCfdiDefecto: '', MetodoPago: '', FormaPago: '', Moneda: 'MXN',
    EsActivo: true };
}

function createConfigEmpresa() {
  return {
    Pais: 'MEX',
    MonedaNacional: 'MXN',
    ModoClaveSocio: 'AUTOMATICA'
  };
}

export default {
  name: 'EmpresaForm',
  props: {
    item: { type: Object, default: null },
    isEdit: { type: Boolean, default: false }
  },
  emits: ['saved', 'close'],
  data() {
    return {
      form: createEmpresa(),
      configEmpresa: createConfigEmpresa(),
      saving: false,
      error: null,
      activeTab: 'datos',
      regimenesFiscales: [],
      // Fiscal
      fiscal: createFiscal(),
      loadingFiscal: false,
      savingFiscal: false,
      fiscalCargado: false,
      // ERP
      erp: createErp(),
      loadingErp: false,
      savingErp: false,
      testingErp: false,
      erpCargado: false
    };
  },
  computed: {
    revealPasswords() { return String(this.erp.Port ?? '') === '...'; },
    esSapB1() { return this.erp.ErpType === 'SAP_B1'; },
    isSaving() { return this.saving || this.savingFiscal || this.savingErp; },
    isLoading() { return this.loadingFiscal || this.loadingErp; },
    labelGuardar() {
      if (this.activeTab === 'fiscal') return 'Guardar datos fiscales';
      if (this.activeTab === 'erp')    return 'Guardar ERP';
      return this.isEdit ? 'Guardar cambios' : 'Crear empresa';
    }
  },
  async created() {
    if (this.isEdit && this.item) {
      this.form = { ...this.item };
      await this.cargarConfigEmpresa();
    }
    try {
      const r = await axios.get(getApiUrl('/api/CatalogosView/leer_catalogo/RegimenFiscal'));
      this.regimenesFiscales = r.data || [];
    } catch (e) {
      console.error('Error al cargar regímenes fiscales:', e);
    }
  },
  methods: {
    async cargarConfigEmpresa() {
      if (!this.form.EmpresaId) {
        this.configEmpresa = createConfigEmpresa();
        return;
      }

      try {
        const [pais, moneda, modo] = await Promise.all([
          parametroService.leer({ Clave: PARAM_EMP_PAIS, EmpresaId: this.form.EmpresaId }),
          parametroService.leer({ Clave: PARAM_EMP_MONEDA_NACIONAL, EmpresaId: this.form.EmpresaId }),
          parametroService.leer({ Clave: PARAM_SOCIO_CLAVE_MODO, EmpresaId: this.form.EmpresaId })
        ]);

        this.configEmpresa = {
          Pais: pais?.Valor || 'MEX',
          MonedaNacional: moneda?.Valor || 'MXN',
          ModoClaveSocio: modo?.Valor || 'AUTOMATICA'
        };
      } catch (_) {
        this.configEmpresa = createConfigEmpresa();
      }
    },

    async guardarConfigEmpresa(empresaId) {
      const payloads = [
        {
          Clave: PARAM_EMP_PAIS,
          Valor: (this.configEmpresa.Pais || 'MEX').trim().toUpperCase(),
          Descripcion: 'Pais base de operacion de la empresa',
          Tipo: 'string',
          EmpresaId: empresaId
        },
        {
          Clave: PARAM_EMP_MONEDA_NACIONAL,
          Valor: (this.configEmpresa.MonedaNacional || 'MXN').trim().toUpperCase(),
          Descripcion: 'Moneda nacional/base de la empresa',
          Tipo: 'string',
          EmpresaId: empresaId
        },
        {
          Clave: PARAM_SOCIO_CLAVE_MODO,
          Valor: this.configEmpresa.ModoClaveSocio === 'AUTOMATICA' ? 'AUTOMATICA' : 'MANUAL',
          Descripcion: 'Modo de captura de clave para socios de negocio',
          Tipo: 'string',
          EmpresaId: empresaId
        }
      ];

      await Promise.all(payloads.map(item => parametroService.guardar(item)));
    },
    // ── Tab Fiscal ──────────────────────────────────────────────────────────
    async onTabFiscal() {
      this.activeTab = 'fiscal';
      if (!this.fiscalCargado) {
        this.loadingFiscal = true;
        try {
          const data = await empresaFiscalService.leer(this.form.EmpresaId);
          this.fiscal = (data && data.EmpresaId) ? { ...data } : createFiscal();
          this.fiscalCargado = true;
        } catch (e) {
          console.error('Error al cargar fiscal:', e);
          this.fiscal = createFiscal();
        } finally {
          this.loadingFiscal = false;
        }
      }
    },

    async borrarFiscal() {
      if (!confirm('¿Eliminar los datos fiscales de esta empresa?')) return;
      this.savingFiscal = true;
      try {
        await empresaFiscalService.borrar(this.form.EmpresaId);
        this.fiscal = createFiscal();
      } catch (e) {
        console.error('Error al borrar fiscal:', e);
      } finally {
        this.savingFiscal = false;
      }
    },

    // ── Tab ERP ─────────────────────────────────────────────────────────────
    async onTabErp() {
      this.activeTab = 'erp';
      if (!this.erpCargado) {
        this.loadingErp = true;
        try {
          const data = await empresaErpExternoService.leer(this.form.EmpresaId);
          this.erp = (data && data.EmpresaId) ? { ...data } : createErp();
          this.erpCargado = true;
        } catch (e) {
          console.error('Error al cargar ERP externo:', e);
          this.erp = createErp();
        } finally {
          this.loadingErp = false;
        }
      }
    },

    async borrarErp() {
      if (!confirm('¿Eliminar la configuración de ERP externo de esta empresa?')) return;
      this.savingErp = true;
      try {
        await empresaErpExternoService.borrar(this.form.EmpresaId);
        this.erp = createErp();
      } catch (e) {
        console.error('Error al borrar ERP externo:', e);
      } finally {
        this.savingErp = false;
      }
    },

    async probarConexionErp() {
      if (!this.form.EmpresaId) return;
      this.testingErp = true;
      // El overlay del modal usa z-index:9999; Swal por defecto 1060.
      // Subimos el z-index del container del Swal por encima del modal.
      const overModal = {
        didOpen: (popup) => {
          const container = popup && popup.parentElement;
          if (container) container.style.zIndex = 10050;
        }
      };
      try {
        const result = await empresaErpExternoService.probarConexion(this.form.EmpresaId);
        const ok = result && result.Number >= 0;
        const mensaje = (result && result.Message) || (ok ? 'Conexión exitosa' : 'No se pudo conectar al ERP');
        this.testingErp = false;
        if (ok) {
          await AlertService.success(mensaje, 'Conexión ERP', overModal);
        } else {
          await AlertService.error(mensaje, 'Error de conexión ERP', overModal);
        }
      } catch (e) {
        console.error('Error al probar conexión ERP:', e);
        this.testingErp = false;
        await AlertService.error('Error de conexión con el servidor', 'Error de conexión ERP', overModal);
      } finally {
        this.testingErp = false;
      }
    },

    // ── Submit ──────────────────────────────────────────────────────────────
    async handleSubmit() {
      this.error = null;
      if (this.activeTab === 'fiscal') { await this.saveFiscal(); return; }
      if (this.activeTab === 'erp')    { await this.saveErp();    return; }
      await this.saveEmpresa();
    },

    async saveEmpresa() {
      if (!this.isEdit && (!this.form.EmpresaId || this.form.EmpresaId < 1)) {
        this.error = 'EmpresaId es obligatorio para nuevas empresas';
        return;
      }
      if (!this.form.Codigo || !this.form.Nombre) {
        this.error = 'Código y Nombre son obligatorios';
        return;
      }
      this.saving = true;
      try {
        const result = await empresaService.guardar(this.form);
        if (result.Number >= 0) {
          if (result.Data?.EmpresaId) {
            this.form = { ...this.form, ...result.Data };
          }
          if (this.form.EmpresaId) {
            await this.guardarConfigEmpresa(this.form.EmpresaId);
          }
          showToast(result.Message || 'Guardado correctamente', result.Type || 'success');
          this.$emit('saved');
        } else { this.error = result.Message || 'Error al guardar'; }
      } catch (e) {
        this.error = 'Error de conexión con el servidor';
      } finally {
        this.saving = false;
      }
    },

    async saveFiscal() {
      if (!this.fiscal.RazonSocial) {
        this.error = 'La razón social es obligatoria';
        return;
      }
      this.savingFiscal = true;
      try {
        const payload = { ...this.fiscal, EmpresaId: this.form.EmpresaId };
        const result = await empresaFiscalService.guardar(payload);
        if (result && result.Number >= 0) {
          this.fiscal.EmpresaId = this.form.EmpresaId;
        } else {
          this.error = (result && result.Message) || 'Error al guardar datos fiscales';
        }
      } catch (e) {
        this.error = 'Error de conexión con el servidor';
      } finally {
        this.savingFiscal = false;
      }
    },

    async saveErp() {
      if (!this.erp.ErpType) {
        this.error = 'El tipo de ERP es obligatorio';
        return;
      }
      this.savingErp = true;
      try {
        const portVal = parseInt(this.erp.Port, 10);
      const payload = { ...this.erp, EmpresaId: this.form.EmpresaId, TipoBsPartida: !!this.erp.TipoBsPartida,
        Port: isNaN(portVal) ? null : portVal };
        const result = await empresaErpExternoService.guardar(payload);
        if (result && result.Number >= 0) {
          this.erp.EmpresaId = this.form.EmpresaId;
        } else {
          this.error = (result && result.Message) || 'Error al guardar ERP';
        }
      } catch (e) {
        this.error = 'Error de conexión con el servidor';
      } finally {
        this.savingErp = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

/* ── Modal ───────────────────────────────────────────────────────────────── */
.emp-modal {
  max-width: 760px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.emp-modal form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.emp-modal .mod-modal-body { flex: 1; overflow-y: auto; }
.emp-modal .mod-modal-footer { flex-shrink: 0; }

.mod-help {
  display: block;
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.25;
}

/* ── Tabs ────────────────────────────────────────────────────────────────── */
.emp-tabs {
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  padding: 0 1.25rem;
  background: #f8fafc;
}
.emp-tab {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.emp-tab:hover:not(:disabled) { color: #1272c4; }
.emp-tab-active { color: #1272c4; border-bottom-color: #1272c4; }
.emp-tab:disabled { opacity: 0.45; cursor: not-allowed; }
.emp-tab-lock { font-size: 0.7rem; margin-left: 0.15rem; }

/* ── Header de estado (compartido fiscal/ERP) ────────────────────────────── */
.emp-erp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0 1rem;
  gap: 1rem;
}
.emp-erp-status {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.83rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
}
.emp-erp-status-ok    { background: #f0fff4; color: #276749; }
.emp-erp-status-empty { background: #f7fafc; color: #718096; }

/* ── Separadores de sección dentro del form ──────────────────────────────── */
.emp-section-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.5rem 0 0;
  border-top: 1px solid #e2e8f0;
  margin-top: 0.25rem;
}
.emp-section-title i { color: #1272c4; }

/* ── Botón eliminar ──────────────────────────────────────────────────────── */
.emp-btn-danger {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
.emp-btn-danger:hover:not(:disabled) { background: #fed7d7; }
.emp-btn-danger:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Botón probar conexión ───────────────────────────────────────────────── */
.emp-btn-test {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.3rem 0.75rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #1272c4;
  background: #ebf8ff;
  border: 1px solid #90cdf4;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}
.emp-btn-test:hover:not(:disabled) { background: #bee3f8; }
.emp-btn-test:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Overlay: probando conexión ERP ─────────────────────────────────────── */
.erp-test-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 40, 0.65);
  backdrop-filter: blur(3px);
  z-index: 10100;
  display: flex;
  align-items: center;
  justify-content: center;
}
.erp-test-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  background: #ffffff;
  padding: 2.25rem 2.75rem;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
.erp-test-spinner {
  width: 42px;
  height: 42px;
  border: 4px solid #cbe3fb;
  border-top-color: #1272c4;
  border-radius: 50%;
  animation: erp-test-spin 0.8s linear infinite;
}
@keyframes erp-test-spin {
  to { transform: rotate(360deg); }
}
.erp-test-msg {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1a365d;
}
.erp-test-sub {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}
.erp-test-fade-enter-active, .erp-test-fade-leave-active { transition: opacity 0.15s; }
.erp-test-fade-enter-from, .erp-test-fade-leave-to { opacity: 0; }

/* ── Select ──────────────────────────────────────────────────────────────── */
.mod-select {
  width: 100%;
  padding: 0.45rem 0.6rem;
  font-size: 0.88rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: white;
  color: #2d3748;
}
.mod-select:focus { outline: none; border-color: #1272c4; }
</style>
